import { type FunctionComponent } from 'react';
import { Loading } from '../../common-components/loading/loading.component';
import { BaseButton, type BaseButtonProps } from '../base-button/base-button.component';
import { BUTTON_STYLE_MAP, DISABLED_BUTTON_STYLE_MAP, type ButtonSize, type ButtonStyle } from '../button.types';
/**
 * StyledButton takes all props base button does except `className` and narrows
 * the `size` prop to not accept null.  This is to reduce inconsistency.
 */
export type StyledButtonProps = {
	buttonStyle?: Extract<
		ButtonStyle,
		'PRIMARY' | 'PRIMARY_ACCENT' | 'SECONDARY' | 'INTERNAL' | 'DANGER' | 'INTERNAL_SECONDARY' | 'TERTIARY' | 'INTERNAL_TERTIARY'
	>;
	size?: NonNullable<Extract<ButtonSize, 'DEFAULT' | 'SMALL' | 'LARGE'>>;
	isBusy?: boolean;
	busyText?: string;
} & Omit<BaseButtonProps, 'className' | 'size'>;

// classes that apply to all buttons, regardless of style or state
const BASE_CLASSES = 'content-center flex items-center justify-center';

function chooseBusyStyle(specifiedStyle: ButtonStyle): string {
	if (specifiedStyle === 'SECONDARY') {
		return BUTTON_STYLE_MAP.BUSY_SECONDARY;
	} else if (specifiedStyle === 'TERTIARY') {
		return BUTTON_STYLE_MAP.BUSY_TERTIARY;
	} else {
		return BUTTON_STYLE_MAP.BUSY_PRIMARY;
	}
}

function chooseDisabledStyle(specifiedStyle: ButtonStyle): string {
	const baseDisabledStyle = BUTTON_STYLE_MAP.DISABLED;
	if (specifiedStyle === 'SECONDARY' || specifiedStyle === 'INTERNAL_SECONDARY') {
		return `${DISABLED_BUTTON_STYLE_MAP.SECONDARY} ${baseDisabledStyle}`;
	} else if (specifiedStyle === 'TERTIARY' || specifiedStyle === 'INTERNAL_TERTIARY') {
		return `${baseDisabledStyle} ${DISABLED_BUTTON_STYLE_MAP.TERTIARY}`;
	} else {
		return `${baseDisabledStyle} ${DISABLED_BUTTON_STYLE_MAP.PRIMARY}`;
	}
}

function chooseClasses(specifiedStyle: ButtonStyle, isBusy: boolean | undefined, disabled: boolean | undefined): string {
	if (isBusy) {
		return `${BASE_CLASSES} ${chooseBusyStyle(specifiedStyle)}`;
	} else if (disabled) {
		return `${BASE_CLASSES} ${chooseDisabledStyle(specifiedStyle)}`;
	} else {
		return `${BASE_CLASSES} ${BUTTON_STYLE_MAP[specifiedStyle]}`;
	}
}

export const StyledButton: FunctionComponent<StyledButtonProps> = ({
	buttonStyle = 'PRIMARY',
	isBusy,
	busyText = '',
	children,
	disabled,
	...remainingProps
}) => {
	if (buttonStyle === 'PRIMARY_ACCENT') {
		// Note: This is done so the text can be animated on click
		children = <div data-testid="primary-accent-content">{children}</div>;
	}
	const busyContent = (
		<div className="justify-center flex gap-2 items-center">
			<span className="hidden items-center theme-luxury:flex!">
				<Loading />
			</span>
			<span>{busyText}</span>
		</div>
	);
	const isDisabled = disabled || isBusy;
	const classes = chooseClasses(buttonStyle, isBusy, disabled);
	return (
		<BaseButton disabled={isDisabled} className={classes} {...remainingProps}>
			{isBusy ? busyContent : children}
		</BaseButton>
	);
};
